/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { Result, message } from 'antd';
import { Button } from '@/uiKit';
import React from 'react';
import { useRefresh } from '@/provider/refresh';
import './PermissionError.less';
import {
  AccessControlRole,
  UpsertUserToDepartmentMutation
} from '@/generated/graphql';
import Mutations from '@/graphql/Mutations';
import { useAppSelector } from '@/stores/hooks';
import { userSelector } from '@/stores/slices/user';
import AppUtil from '@/utils/AppUtil';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import Container from './Container';

interface PermissionErrorProps {
  title?: string;
  subTitle?: string;
}

function PermissionError({ title, subTitle }: PermissionErrorProps) {
  const { refresh } = useRefresh();
  const { departmentId } = useParams();
  const { username } = useAppSelector(userSelector);

  const [addCollaborator, addCollaboratorProps] =
    useMutation<UpsertUserToDepartmentMutation>(
      Mutations.AddDepartmentCollaborator()
    );
  const isSupportUser = AppUtil.isSupportUser(username);

  return (
    <Container>
      <div data-cy="permission-error" className="permission-error">
        <Result
          status="403"
          title={title || 'Cannot access the department'}
          subTitle={
            subTitle ||
            'Sorry, you are not authorized to access this department. Please contact department admin for access.'
          }
          extra={
            isSupportUser ? (
              <Button
                type="primary"
                data-cy="add-collaborator-support-user"
                loading={addCollaboratorProps.loading}
                onClick={() => {
                  addCollaborator({
                    variables: {
                      departmentId,
                      userId: username,
                      role: AccessControlRole.Admin
                    }
                  })
                    .then(() => {
                      message.success('Added successfully');
                      refresh();
                    })
                    .catch(() => {
                      message.info('Failed to add collaborator');
                    });
                }}
              >
                Click to become a collaborator
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  refresh();
                }}
              >
                Refresh
              </Button>
            )
          }
        />
      </div>
    </Container>
  );
}

export default PermissionError;
